import { useChat } from "@ai-sdk/react";
import type { inferRouterOutputs } from "@trpc/server";
import { DefaultChatTransport, type UIMessage } from "ai";
import { AlertTriangle, LifeBuoy, RefreshCw, Sparkles } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { AppRouter } from "@runcheetah/api/router";
import { Button } from "@runcheetah/ui";
import {
  parseBrandAssistantActivityUpdatePart,
  type BrandAssistantActivityUpdateData,
} from "@runcheetah/shared";

import { AssistantBrandProfileEditPreviews } from "@/components/chat/assistant-brand-profile-edit-previews";
import {
  appendAssistantLiveActivityUpdate,
  AssistantChatPanel,
  getLatestAssistantMessageAfterLatestUser,
  getAssistantMessageText,
  getAssistantTraceEntries,
  getPersistedAssistantStreamActivityUpdates,
  hasActiveAssistantMessagePart,
  hasActivePersistedAssistantStream,
  shouldRenderAssistantStructuredExtras,
} from "@/components/chat/assistant-chat";
import { AssistantInlineStatus } from "@/components/chat/assistant-inline-status";
import { AssistantLiveActivity } from "@/components/chat/assistant-live-activity";
import { AssistantOnboardingCompletionCards } from "@/components/chat/assistant-onboarding-completion-cards";
import { CenteredLoader } from "@/components/common";
import { buildBrandPath } from "@/hooks/use-route-brand";
import { useMarkBrandAssistantChatSeen } from "@/hooks/use-mark-brand-assistant-chat-seen";
import { ASSISTANT_ROUTE_CATALOG } from "@/lib/app-route-manifest";
import {
  getAssistantBrandProfileEditPreviewReferences,
  getAssistantBrandProfileEditPreviewResolutionStatuses,
  getAssistantOnboardingProfilePreviewGate,
  getLatestOnboardingBrandProfileEditPreviewId,
  type AssistantBrandProfileEditPreviewResolutionStatus,
  type AssistantOnboardingProfilePreviewGate,
} from "@/lib/assistant-brand-profile-edit-previews";
import { getAssistantOnboardingCompletionReferences } from "@/lib/assistant-onboarding-completion";
import { buildBrandAssistantSendMessagesRequestBody } from "@/lib/brand-assistant-chat-request";
import { CHEETAH_SQUARE_LOGO_SRC } from "@/lib/brand-assets";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type BrandSummary = RouterOutputs["brand"]["getById"]["brand"];
type OnboardingSummary = RouterOutputs["brand"]["getById"]["onboarding"];

const EMPTY_MESSAGES: UIMessage[] = [];
const PENDING_CHAT_ID = "00000000-0000-4000-8000-000000000000";
const ONBOARDING_COMPLETION_RETRY_LIMIT = 12;
const ONBOARDING_COMPLETION_RETRY_INTERVAL_MS = 2_500;
const ONBOARDING_PROFILE_PREVIEW_RETRY_LIMIT = 3;
const ONBOARDING_PROFILE_PREVIEW_RETRY_INTERVAL_MS = 2_500;
const FINAL_ONBOARDING_STATUS_COPY_INTERVAL_MS = 3_200;

export function BrandOnboardingChatStage({
  brand,
  onboarding,
  status,
}: {
  brand: BrandSummary;
  onboarding: OnboardingSummary;
  status: BrandSummary["status"];
}) {
  const intl = useIntl();
  const utils = trpc.useUtils();
  const [draft, setDraft] = useState("");
  const [liveActivityUpdates, setLiveActivityUpdates] = useState<
    BrandAssistantActivityUpdateData[]
  >([]);
  const autoSentChatRef = useRef<string | null>(null);
  const requestedProfilePreviewChatRef = useRef<string | null>(null);
  const profilePreviewRetryCountsRef = useRef<Record<string, number>>({});
  const lastLoadedChatRef = useRef<string | null>(null);
  const requestedChatCreationBrandRef = useRef<string | null>(null);
  const requestedCompletionSignatureRef = useRef<string | null>(null);
  const [completionRetryTick, setCompletionRetryTick] = useState(0);
  const [profilePreviewRetryTick, setProfilePreviewRetryTick] = useState(0);
  const [locallyResolvedOnboardingProfilePreview, setLocallyResolvedOnboardingProfilePreview] =
    useState<{
      previewId: string;
      status: AssistantBrandProfileEditPreviewResolutionStatus;
    } | null>(null);
  const isOnboardingRetryableFailure = shouldShowOnboardingRecoveryCard({
    status,
    stage: onboarding.stage,
  });

  const chatListQuery = trpc.brandAssistantChat.list.useQuery(
    { brandId: brand.id },
    { staleTime: 0, refetchOnWindowFocus: true },
  );
  const createChatMutation = trpc.brandAssistantChat.create.useMutation({
    onSuccess: async () => {
      await utils.brandAssistantChat.list.invalidate({ brandId: brand.id });
    },
  });
  const onboardingChat = chatListQuery.data?.chats[0] ?? null;
  const chatId = onboardingChat?.id ?? PENDING_CHAT_ID;
  const chatQuery = trpc.brandAssistantChat.get.useQuery(
    { brandId: brand.id, chatId },
    {
      enabled: Boolean(onboardingChat?.id),
      staleTime: 0,
      refetchOnWindowFocus: true,
    },
  );
  const chatData = chatQuery.data as
    | {
        chat: {
          id: string;
          messages: UIMessage[];
          assistantContentVersion: number;
          seenAssistantContentVersion: number;
        } | null;
      }
    | undefined;
  const activeChat = chatData?.chat ?? null;
  useMarkBrandAssistantChatSeen({ brandId: brand.id, chat: activeChat });
  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${import.meta.env.VITE_API_URL ?? ""}/api/chat/brand-onboarding`,
        credentials: "include",
        prepareSendMessagesRequest({ messages, trigger }) {
          return {
            body: buildBrandAssistantSendMessagesRequestBody({
              brandId: brand.id,
              chatId,
              routeCatalog: ASSISTANT_ROUTE_CATALOG,
              trigger,
              messages,
            }),
          };
        },
      }),
    [brand.id, chatId],
  );
  const {
    messages,
    sendMessage,
    status: chatStatus,
    stop,
    error,
    setMessages,
    clearError,
  } = useChat({
    id: `brand-onboarding-${brand.id}-${chatId}`,
    messages: activeChat?.messages ?? EMPTY_MESSAGES,
    transport,
    experimental_throttle: 250,
    onData(dataPart) {
      const update = parseBrandAssistantActivityUpdatePart(dataPart)?.data;
      if (update) {
        setLiveActivityUpdates((currentUpdates) =>
          appendAssistantLiveActivityUpdate(currentUpdates, update),
        );
      }
    },
  });
  const isBusy = chatStatus === "submitted" || chatStatus === "streaming";
  const hasPersistedStream = hasActivePersistedAssistantStream(messages);
  const activeChatHasPersistedStream = hasActivePersistedAssistantStream(
    activeChat?.messages ?? EMPTY_MESSAGES,
  );
  const effectiveChatStatus = isBusy || hasPersistedStream ? "streaming" : chatStatus;
  const isChatBusy = isBusy || hasPersistedStream;
  const latestAssistantMessage = useMemo(
    () => getLatestAssistantMessageAfterLatestUser(messages),
    [messages],
  );
  const persistedStreamActivityUpdates = useMemo(
    () => getPersistedAssistantStreamActivityUpdates(messages),
    [messages],
  );
  const profilePreviewResolutionStatuses = useMemo(
    () => getAssistantBrandProfileEditPreviewResolutionStatuses(messages),
    [messages],
  );
  const onboardingProfilePreviewGate = useMemo(
    () => getAssistantOnboardingProfilePreviewGate(messages),
    [messages],
  );
  const activeOnboardingProfilePreviewId = useMemo(
    () => getLatestOnboardingBrandProfileEditPreviewId(messages),
    [messages],
  );
  const hasProfilePreviewFallbackContext = useMemo(
    () => hasOnboardingProfilePreviewFallbackContext(messages),
    [messages],
  );
  const {
    hasCompleted: hasCompletedOnboardingProfilePreview,
    hasPending: hasPendingOnboardingProfilePreview,
    hasLocallyResolved: hasLocallyResolvedOnboardingProfilePreview,
  } = getEffectiveOnboardingProfilePreviewCompletionState({
    profilePreviewGateStatus: onboardingProfilePreviewGate.status,
    profileUpdatesState: onboarding.completion.profileUpdates.state,
    activePreviewId: activeOnboardingProfilePreviewId,
    locallyResolvedPreviewId: locallyResolvedOnboardingProfilePreview?.previewId ?? null,
  });
  const canRequestCompletion = status === "processing" || status === "scanned";
  const shouldRequestFinalCompletions =
    canRequestCompletion &&
    !hasPendingOnboardingProfilePreview &&
    (hasCompletedOnboardingProfilePreview || onboarding.completion.firstAssetReady);
  const shouldRequestCompletions = shouldRequestFinalCompletions;
  const isPreparingChat =
    chatListQuery.isPending ||
    createChatMutation.isPending ||
    (Boolean(onboardingChat?.id) && chatQuery.isPending && !activeChat);
  const hasMessages = messages.length > 0;
  const isOnboardingActive = status === "queued" || status === "processing";
  const shouldShowOnboardingProgress =
    isOnboardingActive && onboarding.stage !== "ready" && !isOnboardingRetryableFailure;
  const hasVisibleLiveOnboardingProgress = useMemo(
    () =>
      messages.some((message) =>
        getAssistantOnboardingCompletionReferences(message, {
          messages,
          visibleAfterMessageIndex: onboardingProfilePreviewGate.resolvedAtMessageIndex,
        }).some(isLiveOnboardingProgressCompletion),
      ),
    [messages, onboardingProfilePreviewGate.resolvedAtMessageIndex],
  );
  const liveActivity = useMemo(() => {
    const updates =
      liveActivityUpdates.length > 0 ? liveActivityUpdates : persistedStreamActivityUpdates;
    return isChatBusy && updates.length > 0 ? <AssistantLiveActivity updates={updates} /> : null;
  }, [isChatBusy, liveActivityUpdates, persistedStreamActivityUpdates]);
  const requestCompletionMutation = trpc.brandAssistantChat.requestOnboardingCompletion.useMutation(
    {
      onSuccess: async (data) => {
        await Promise.all([
          activeChat?.id
            ? utils.brandAssistantChat.get.invalidate({ brandId: brand.id, chatId: activeChat.id })
            : Promise.resolve(),
          utils.brandAssistantChat.list.invalidate({ brandId: brand.id }),
          utils.brand.getById.invalidate({ brandId: brand.id }),
        ]);

        if (activeChat?.id && data.chat) {
          setMessages(
            preserveStreamingAssistantMessageText({
              currentMessages: messages,
              nextMessages: data.chat.messages as unknown as UIMessage[],
              isStreaming: isChatBusy,
            }),
          );
        }
      },
    },
  );
  const requestProfilePreviewMutation =
    trpc.brandAssistantChat.requestOnboardingProfilePreview.useMutation({
      onSuccess: async (data) => {
        await Promise.all([
          activeChat?.id
            ? utils.brandAssistantChat.get.invalidate({ brandId: brand.id, chatId: activeChat.id })
            : Promise.resolve(),
          utils.brandAssistantChat.list.invalidate({ brandId: brand.id }),
          utils.brand.getById.invalidate({ brandId: brand.id }),
        ]);

        if (activeChat?.id && data.chat) {
          const nextMessages = preserveStreamingAssistantMessageText({
            currentMessages: messages,
            nextMessages: data.chat.messages as unknown as UIMessage[],
            isStreaming: isChatBusy,
          });
          setMessages(nextMessages);

          if (getAssistantOnboardingProfilePreviewGate(nextMessages).status === "missing") {
            window.setTimeout(() => {
              if (requestedProfilePreviewChatRef.current === activeChat.id) {
                requestedProfilePreviewChatRef.current = null;
              }
              setProfilePreviewRetryTick((currentTick) => currentTick + 1);
            }, ONBOARDING_PROFILE_PREVIEW_RETRY_INTERVAL_MS);
          }
        }
      },
    });
  const retryOnboardingMutation = trpc.brand.retryMine.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.brand.getById.invalidate({ brandId: brand.id }),
        utils.brand.listMine.invalidate(),
        utils.brandAssistantChat.list.invalidate({ brandId: brand.id }),
      ]);
    },
  });
  const requestSupportMutation = trpc.brand.requestOnboardingSupport.useMutation();
  const shouldShowFinalOnboardingStatus = shouldShowOnboardingFinalStatus({
    isChatBusy,
    hasVisibleLiveOnboardingProgress,
    isCompletionPending: requestCompletionMutation.isPending,
    isProfilePreviewResolved: hasCompletedOnboardingProfilePreview,
    shouldShowOnboardingProgress,
    firstAssetReady: onboarding.completion.firstAssetReady,
  });
  useEffect(() => {
    if (chatListQuery.isPending || chatListQuery.isError || createChatMutation.isPending) {
      return;
    }

    if ((chatListQuery.data?.chats.length ?? 0) > 0) {
      return;
    }

    if (requestedChatCreationBrandRef.current === brand.id) {
      return;
    }

    requestedChatCreationBrandRef.current = brand.id;
    createChatMutation.mutate(
      { brandId: brand.id, context: "onboarding" },
      {
        onError: () => {
          if (requestedChatCreationBrandRef.current === brand.id) {
            requestedChatCreationBrandRef.current = null;
          }
        },
      },
    );
  }, [
    brand.id,
    chatListQuery.data?.chats.length,
    chatListQuery.isError,
    chatListQuery.isPending,
    createChatMutation,
  ]);

  useEffect(() => {
    if (!activeChat || lastLoadedChatRef.current === activeChat.id) {
      return;
    }

    lastLoadedChatRef.current = activeChat.id;
    setMessages(activeChat.messages as unknown as UIMessage[]);
    setDraft("");
    setLiveActivityUpdates([]);
    setLocallyResolvedOnboardingProfilePreview(null);
    clearError();
  }, [activeChat, clearError, setMessages]);

  useEffect(() => {
    if (!isBusy) {
      setLiveActivityUpdates([]);
    }
  }, [isBusy]);

  useEffect(() => {
    if (
      !locallyResolvedOnboardingProfilePreview ||
      locallyResolvedOnboardingProfilePreview.previewId === activeOnboardingProfilePreviewId
    ) {
      return;
    }

    setLocallyResolvedOnboardingProfilePreview(null);
  }, [activeOnboardingProfilePreviewId, locallyResolvedOnboardingProfilePreview]);

  useEffect(() => {
    if (!activeChat?.id || isChatBusy || requestProfilePreviewMutation.isPending) {
      return;
    }

    if (requestedProfilePreviewChatRef.current === activeChat.id) {
      return;
    }

    const retryCount = profilePreviewRetryCountsRef.current[activeChat.id] ?? 0;
    if (retryCount >= ONBOARDING_PROFILE_PREVIEW_RETRY_LIMIT) {
      return;
    }

    if (onboardingProfilePreviewGate.status !== "missing" || !hasProfilePreviewFallbackContext) {
      return;
    }

    requestedProfilePreviewChatRef.current = activeChat.id;
    profilePreviewRetryCountsRef.current[activeChat.id] = retryCount + 1;
    requestProfilePreviewMutation.mutate(
      { brandId: brand.id, chatId: activeChat.id },
      {
        onError: () => {
          if (requestedProfilePreviewChatRef.current === activeChat.id) {
            requestedProfilePreviewChatRef.current = null;
          }
        },
      },
    );
  }, [
    activeChat?.id,
    brand.id,
    isChatBusy,
    onboardingProfilePreviewGate.status,
    profilePreviewRetryTick,
    requestProfilePreviewMutation,
    hasProfilePreviewFallbackContext,
  ]);

  useEffect(() => {
    if (!activeChat?.id || !hasPersistedStream) {
      return;
    }

    const interval = window.setInterval(() => {
      void utils.brandAssistantChat.get.invalidate({ brandId: brand.id, chatId: activeChat.id });
      void utils.brandAssistantChat.list.invalidate({ brandId: brand.id });
    }, 1_000);

    return () => window.clearInterval(interval);
  }, [
    activeChat?.id,
    brand.id,
    hasPersistedStream,
    utils.brandAssistantChat.get,
    utils.brandAssistantChat.list,
  ]);

  useEffect(() => {
    if (isBusy || !activeChat || (!hasPersistedStream && !activeChatHasPersistedStream)) {
      return;
    }

    setMessages(activeChat.messages as unknown as UIMessage[]);
  }, [activeChat, activeChatHasPersistedStream, hasPersistedStream, isBusy, setMessages]);

  useEffect(() => {
    if (!activeChat || isOnboardingRetryableFailure || !shouldRequestCompletions) {
      return;
    }

    const signature = JSON.stringify({
      chatId: activeChat.id,
      onboardingStage: onboarding.stage,
      strategy: onboarding.completion.strategy.state,
      strategyReady: onboarding.completion.strategy.ready,
      strategyUpdatedAt: onboarding.completion.strategy.updatedAt,
      profileUpdates: onboarding.completion.profileUpdates.state,
      profileUpdateCount: onboarding.completion.profileUpdates.pendingChangeCount,
      profilePreviewGate: onboardingProfilePreviewGate.status,
      profilePreviewResolvedAt: onboardingProfilePreviewGate.resolvedAtMessageIndex,
      locallyResolvedProfilePreview: locallyResolvedOnboardingProfilePreview,
      visualIdentityReady: onboarding.completion.visualIdentity.ready,
      firstAssetPromptPending: !onboarding.completion.firstAssetReady,
      firstAssetReady: onboarding.completion.firstAssetReady,
      retryTick: completionRetryTick,
    });

    if (requestedCompletionSignatureRef.current === signature) {
      return;
    }

    requestedCompletionSignatureRef.current = signature;
    requestCompletionMutation.mutate(
      { brandId: brand.id, chatId: activeChat.id },
      {
        onError: () => {
          requestedCompletionSignatureRef.current = null;
        },
      },
    );
  }, [
    activeChat,
    brand.id,
    completionRetryTick,
    hasCompletedOnboardingProfilePreview,
    hasLocallyResolvedOnboardingProfilePreview,
    isOnboardingRetryableFailure,
    locallyResolvedOnboardingProfilePreview,
    onboarding,
    onboardingProfilePreviewGate.resolvedAtMessageIndex,
    onboardingProfilePreviewGate.status,
    requestCompletionMutation,
    shouldRequestCompletions,
    status,
  ]);

  useEffect(() => {
    if (
      !activeChat ||
      isOnboardingRetryableFailure ||
      !shouldRequestCompletions ||
      completionRetryTick >= ONBOARDING_COMPLETION_RETRY_LIMIT ||
      requestCompletionMutation.isPending
    ) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setCompletionRetryTick((currentTick) => currentTick + 1);
    }, ONBOARDING_COMPLETION_RETRY_INTERVAL_MS);

    return () => window.clearTimeout(timeout);
  }, [
    activeChat,
    completionRetryTick,
    isOnboardingRetryableFailure,
    requestCompletionMutation.isPending,
    shouldRequestCompletions,
  ]);

  useEffect(() => {
    if (
      !activeChat ||
      isOnboardingRetryableFailure ||
      activeChat.messages.length > 0 ||
      messages.length > 0 ||
      isChatBusy ||
      autoSentChatRef.current === activeChat.id
    ) {
      return;
    }

    autoSentChatRef.current = activeChat.id;

    void (async () => {
      clearError();
      await sendMessage({ parts: [{ type: "text", text: brand.sourceUrl }] as UIMessage["parts"] });
    })();
  }, [
    activeChat,
    brand.sourceUrl,
    clearError,
    isChatBusy,
    isOnboardingRetryableFailure,
    messages.length,
    sendMessage,
  ]);

  const sendPrompt = async (prompt: string) => {
    if (!activeChat || isChatBusy) {
      return;
    }

    clearError();
    await sendMessage({ parts: [{ type: "text", text: prompt }] as UIMessage["parts"] });
  };

  const recoveryCard = isOnboardingRetryableFailure ? (
    <OnboardingRecoveryCard
      isRetrying={retryOnboardingMutation.isPending}
      isRequestingSupport={requestSupportMutation.isPending}
      supportRequested={Boolean(requestSupportMutation.data)}
      error={
        retryOnboardingMutation.error?.message ?? requestSupportMutation.error?.message ?? null
      }
      onRetry={() =>
        retryOnboardingMutation.mutate({ brandId: brand.id, sourceUrl: brand.sourceUrl })
      }
      onRequestSupport={() => requestSupportMutation.mutate({ brandId: brand.id })}
    />
  ) : null;

  return (
    <div className="flex h-full min-h-0 flex-col bg-[radial-gradient(circle_at_50%_34%,rgba(190,255,0,0.10),transparent_28%),linear-gradient(180deg,var(--background),var(--background))] lg:flex-row">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {isPreparingChat ? (
          <CenteredLoader />
        ) : (
          <AssistantChatPanel
            messages={messages}
            status={effectiveChatStatus}
            draft={draft}
            error={error}
            showThreadToolbar={hasMessages}
            className="min-h-0 flex-1 bg-transparent shadow-none backdrop-blur-0 xl:shadow-none"
            contentClassName={cn(
              hasMessages
                ? "mx-auto w-full max-w-4xl px-6 py-8"
                : "flex items-center justify-center px-6 pb-20 pt-10",
            )}
            composerContainerClassName="mx-auto w-full max-w-4xl border-t-0 bg-transparent px-4 pb-2 sm:px-6"
            extraContent={
              recoveryCard ??
              (shouldShowFinalOnboardingStatus ? <FinalOnboardingStatusMessage /> : undefined)
            }
            assistantMessageContentClassName="motion-safe:animate-[assistant-onboarding-slide-down_240ms_ease-out]"
            assistantMessageExtraClassName="motion-safe:animate-[assistant-onboarding-slide-down_240ms_ease-out]"
            renderAssistantMessageExtra={(message) => {
              const shouldRenderMessageStructuredExtras = shouldRenderOnboardingStructuredExtras({
                isBusy: isChatBusy,
                messageId: message.id,
                latestAssistantMessageId: latestAssistantMessage?.id ?? null,
                hasActiveMessagePart: hasActiveAssistantMessagePart(message),
              });
              const profileEditPreviews = shouldRenderMessageStructuredExtras
                ? getAssistantBrandProfileEditPreviewReferences(message).filter(
                    (reference) =>
                      reference.confirmationSource !== "chatbot_onboarding" ||
                      reference.id === activeOnboardingProfilePreviewId,
                  )
                : [];
              const onboardingCompletions = getAssistantOnboardingCompletionReferences(message, {
                messages,
                visibleAfterMessageIndex: onboardingProfilePreviewGate.resolvedAtMessageIndex,
              });
              const visibleOnboardingCompletions = shouldRenderMessageStructuredExtras
                ? onboardingCompletions
                : [];
              if (profileEditPreviews.length === 0 && visibleOnboardingCompletions.length === 0) {
                return null;
              }

              return (
                <div className="space-y-4">
                  <AssistantBrandProfileEditPreviews
                    brandId={brand.id}
                    chatId={activeChat?.id ?? null}
                    references={profileEditPreviews}
                    resolutionStatuses={profilePreviewResolutionStatuses}
                    onOnboardingPreviewResolved={(resolution) => {
                      if (resolution.messages) {
                        setMessages((currentMessages) =>
                          preserveStreamingAssistantMessageText({
                            currentMessages,
                            nextMessages: resolution.messages ?? [],
                            isStreaming: isChatBusy,
                          }),
                        );
                      }
                      setLocallyResolvedOnboardingProfilePreview({
                        previewId: resolution.previewId,
                        status: resolution.status,
                      });
                    }}
                  />
                  <AssistantOnboardingCompletionCards
                    completions={visibleOnboardingCompletions}
                    pulseHref={buildBrandPath(brand.id, "/pulse")}
                  />
                </div>
              );
            }}
            shouldRenderAssistantMessageContent={shouldRenderOnboardingAssistantMessageContent}
            getAssistantMessageContent={getOnboardingAssistantMessageContent}
            placeholder={intl.formatMessage({
              id: isOnboardingRetryableFailure
                ? "brandOnboarding.chat.failedPlaceholder"
                : "brandOnboarding.chat.placeholder",
              defaultMessage: isOnboardingRetryableFailure
                ? "Retry setup or notify support to continue."
                : "Message Cheetah...",
            })}
            runtimeActivity={liveActivity}
            emptyState={
              recoveryCard ?? <BrandOnboardingChatEmptyState sourceDomain={brand.sourceDomain} />
            }
            disableRuntimeThoughts
            showActivityParts={false}
            onDraftChange={setDraft}
            onSubmit={sendPrompt}
            onStop={stop}
            onClearError={clearError}
          />
        )}

        <p className="pb-6 text-center text-xs text-muted-foreground">
          <FormattedMessage
            id="pulse.footer.disclaimer"
            defaultMessage="Cheetah can make mistakes. Review anything before it ships."
          />
        </p>
      </div>
    </div>
  );
}

function OnboardingRecoveryCard({
  isRetrying,
  isRequestingSupport,
  supportRequested,
  error,
  onRetry,
  onRequestSupport,
}: {
  isRetrying: boolean;
  isRequestingSupport: boolean;
  supportRequested: boolean;
  error: string | null;
  onRetry: () => void;
  onRequestSupport: () => void;
}) {
  return (
    <div className="mx-auto w-full max-w-3xl rounded-[1.75rem] border border-amber-300/35 bg-card/95 p-5 text-left shadow-[0_24px_70px_-32px_rgba(15,23,42,0.45)] sm:p-6">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-amber-300/35 bg-amber-200/20 text-amber-700 dark:text-amber-300">
          <AlertTriangle className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1 space-y-4">
          <div className="space-y-2">
            <h2 className="text-balance font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              <FormattedMessage
                id="brandOnboarding.chat.recoveryTitle"
                defaultMessage="We could not finish setting up this brand"
              />
            </h2>
            <p className="text-sm leading-6 text-muted-foreground sm:text-base">
              <FormattedMessage
                id="brandOnboarding.chat.recoveryDescription"
                defaultMessage="Something got in the way while Cheetah was learning your website. You can try again now, or notify support and we will help you get set up."
              />
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button type="button" className="rounded-full" disabled={isRetrying} onClick={onRetry}>
              <RefreshCw
                className={cn("h-4 w-4", isRetrying && "animate-spin")}
                aria-hidden="true"
              />
              {isRetrying ? (
                <FormattedMessage id="brandOnboarding.chat.retrying" defaultMessage="Retrying..." />
              ) : (
                <FormattedMessage
                  id="brandOnboarding.chat.retryOnboarding"
                  defaultMessage="Retry setup"
                />
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              disabled={isRequestingSupport || supportRequested}
              onClick={onRequestSupport}
            >
              <LifeBuoy className="h-4 w-4" aria-hidden="true" />
              {isRequestingSupport ? (
                <FormattedMessage
                  id="brandOnboarding.chat.notifyingSupport"
                  defaultMessage="Notifying support..."
                />
              ) : supportRequested ? (
                <FormattedMessage
                  id="brandOnboarding.chat.supportNotified"
                  defaultMessage="Support notified"
                />
              ) : (
                <FormattedMessage
                  id="brandOnboarding.chat.notifySupport"
                  defaultMessage="Notify support"
                />
              )}
            </Button>
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
      </div>
    </div>
  );
}

function FinalOnboardingStatusMessage() {
  const intl = useIntl();
  const copy = useMemo(
    () => [
      intl.formatMessage({
        id: "brandOnboarding.finalStatus.extractingVisualUniverse",
        defaultMessage: "Extracting your visual universe...",
      }),
      intl.formatMessage({
        id: "brandOnboarding.finalStatus.preparingLaunchContext",
        defaultMessage: "Preparing your launch-ready context...",
      }),
      intl.formatMessage({
        id: "brandOnboarding.finalStatus.wrappingFinalSteps",
        defaultMessage: "Wrapping up the final setup steps...",
      }),
      intl.formatMessage({
        id: "brandOnboarding.finalStatus.almostDone",
        defaultMessage: "Almost done...",
      }),
    ],
    [intl],
  );

  return (
    <AssistantInlineStatus
      details={copy}
      className="max-w-[min(100%,42rem)]"
      intervalMs={FINAL_ONBOARDING_STATUS_COPY_INTERVAL_MS}
    />
  );
}

export function preserveStreamingAssistantMessageText(input: {
  currentMessages: readonly UIMessage[];
  nextMessages: readonly UIMessage[];
  isStreaming: boolean;
}): UIMessage[] {
  if (!input.isStreaming) {
    return [...input.nextMessages];
  }

  const currentAssistant = getLatestAssistantMessageAfterLatestUser(input.currentMessages);
  if (!currentAssistant) {
    return [...input.nextMessages];
  }

  const currentTextParts = currentAssistant.parts.filter(isAssistantTextLikePart);
  if (currentTextParts.length === 0) {
    return [...input.nextMessages];
  }

  const preservedMessages = input.nextMessages.map((message) => {
    if (message.id !== currentAssistant.id || message.role !== "assistant") {
      return message;
    }

    const nextNonTextParts = message.parts.filter((part) => !isAssistantTextLikePart(part));
    return {
      ...message,
      parts: [...currentTextParts, ...nextNonTextParts] as UIMessage["parts"],
    };
  });

  return removeDuplicateStreamingOnboardingAssistantText(preservedMessages);
}

function isAssistantTextLikePart(part: UIMessage["parts"][number]) {
  return part.type === "text" || part.type === "reasoning";
}

function removeDuplicateStreamingOnboardingAssistantText(messages: readonly UIMessage[]) {
  const latestUserIndex = messages.findLastIndex((message) => message.role === "user");
  const seenAssistantText = new Map<string, number>();
  const indexesToRemove = new Set<number>();

  for (let index = latestUserIndex + 1; index < messages.length; index += 1) {
    const message = messages[index];
    if (!message || message.role !== "assistant") {
      continue;
    }

    const key = normalizeOnboardingAssistantText(getOnboardingAssistantMessageContent(message));
    if (!key) {
      continue;
    }

    const existingIndex = seenAssistantText.get(key);
    if (existingIndex === undefined) {
      seenAssistantText.set(key, index);
      continue;
    }

    const existingMessage = messages[existingIndex];
    if (!existingMessage) {
      indexesToRemove.add(index);
      continue;
    }

    if (hasAssistantStructuredParts(message) && !hasAssistantStructuredParts(existingMessage)) {
      indexesToRemove.add(existingIndex);
      seenAssistantText.set(key, index);
      continue;
    }

    indexesToRemove.add(index);
  }

  return messages.filter((_, index) => !indexesToRemove.has(index));
}

function hasAssistantStructuredParts(message: UIMessage) {
  return message.parts.some((part) => !isAssistantTextLikePart(part));
}

export function shouldRenderOnboardingAssistantMessageContent(message: UIMessage) {
  if (message.role !== "assistant") {
    return true;
  }

  const rawText = getAssistantMessageText(message).trim();
  const text = getOnboardingAssistantMessageContent(message).trim();
  const hasFailedProfilePreview = message.parts.some((part) => {
    const candidate = part as { type?: string; state?: string };
    return (
      candidate.type === "tool-preview_brand_profile_edits" && candidate.state === "output-error"
    );
  });

  if (hasFailedProfilePreview) {
    return false;
  }

  if (text) {
    return true;
  }

  if (rawText) {
    return false;
  }

  const hasStructuredOnboardingContent =
    getAssistantBrandProfileEditPreviewReferences(message).length > 0 ||
    getAssistantOnboardingCompletionReferences(message).length > 0 ||
    message.parts.some((part) => {
      const candidate = part as { type?: string };
      return candidate.type === "tool-preview_brand_profile_edits";
    });

  return !hasStructuredOnboardingContent;
}

export function getOnboardingAssistantMessageContent(message: UIMessage) {
  const text = getAssistantMessageText(message).trim();
  if (message.role !== "assistant" || !text) {
    return text;
  }

  const visibleText = text
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph && !isOnboardingProfilePreviewBridgeText(paragraph))
    .join("\n\n");

  return removeRepeatedOnboardingAssistantText(visibleText);
}

function removeRepeatedOnboardingAssistantText(text: string) {
  const paragraphs = text
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  if (paragraphs.length < 2) {
    return text;
  }

  const normalizedParagraphs = paragraphs.map(normalizeOnboardingAssistantText);
  for (let startIndex = 1; startIndex < paragraphs.length; startIndex += 1) {
    let matchCount = 0;
    while (
      startIndex + matchCount < paragraphs.length &&
      normalizedParagraphs[startIndex + matchCount] === normalizedParagraphs[matchCount]
    ) {
      matchCount += 1;
    }

    const repeatedTextLength = paragraphs
      .slice(startIndex, startIndex + matchCount)
      .join(" ").length;
    if (matchCount >= 2 || repeatedTextLength >= 160) {
      return paragraphs.slice(0, startIndex).join("\n\n");
    }
  }

  return text;
}

function normalizeOnboardingAssistantText(text: string) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function shouldRenderOnboardingProfilePreviewCard(input: {
  isBusy: boolean;
  messageId: string;
  latestAssistantMessageId: string | null;
}) {
  return !input.isBusy || input.messageId !== input.latestAssistantMessageId;
}

export function shouldRenderOnboardingStructuredExtras(input: {
  isBusy: boolean;
  messageId: string;
  latestAssistantMessageId: string | null;
  hasActiveMessagePart: boolean;
}) {
  return shouldRenderAssistantStructuredExtras(input);
}

export function shouldShowOnboardingFinalStatus(input: {
  isChatBusy: boolean;
  hasVisibleLiveOnboardingProgress: boolean;
  isCompletionPending: boolean;
  isProfilePreviewResolved: boolean;
  shouldShowOnboardingProgress: boolean;
  firstAssetReady: boolean;
}) {
  return (
    !input.isChatBusy &&
    !input.hasVisibleLiveOnboardingProgress &&
    (input.isCompletionPending ||
      (input.isProfilePreviewResolved &&
        input.shouldShowOnboardingProgress &&
        !input.firstAssetReady))
  );
}

function isLiveOnboardingProgressCompletion(
  completion: ReturnType<typeof getAssistantOnboardingCompletionReferences>[number],
) {
  return (
    completion.kind === "brand_scan_in_progress" ||
    completion.kind === "strategy_generation_in_progress"
  );
}

function hasOnboardingProfilePreviewFallbackContext(messages: readonly UIMessage[]) {
  return messages.some((message) => {
    if (message.role !== "assistant") {
      return false;
    }

    if (getAssistantMessageText(message).trim()) {
      return true;
    }

    return getAssistantTraceEntries(message).some(
      (entry) => entry.state === "done" && !entry.command.startsWith("search"),
    );
  });
}

export function shouldShowOnboardingRecoveryCard(input: {
  status: BrandSummary["status"];
  stage: OnboardingSummary["stage"];
}) {
  return input.status === "failed" || input.stage === "failed";
}

export function shouldShowOnboardingLiveActivity(input: {
  isBusy: boolean;
  isOnboardingActive: boolean;
  hasLiveActivityUpdates: boolean;
  latestLiveActivityAt: number | null;
  latestAssistantTextAt: number | null;
}) {
  if (!input.isBusy || !input.isOnboardingActive || !input.hasLiveActivityUpdates) {
    return false;
  }

  if (input.latestLiveActivityAt === null) {
    return true;
  }

  return (
    input.latestAssistantTextAt === null || input.latestLiveActivityAt > input.latestAssistantTextAt
  );
}

function isOnboardingProfilePreviewBridgeText(text: string) {
  const normalized = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  return (
    normalized === "i have enough evidence to draft the brand profile" ||
    normalized ===
      "i have organized the strongest findings into a brand profile preview review these updates before i apply anything"
  );
}

export function getEffectiveOnboardingProfilePreviewCompletionState(input: {
  profilePreviewGateStatus: AssistantOnboardingProfilePreviewGate["status"];
  profileUpdatesState: OnboardingSummary["completion"]["profileUpdates"]["state"];
  activePreviewId: string | null;
  locallyResolvedPreviewId: string | null;
}) {
  const hasLocallyResolved = Boolean(
    input.activePreviewId && input.locallyResolvedPreviewId === input.activePreviewId,
  );

  return {
    hasLocallyResolved,
    hasCompleted:
      hasLocallyResolved ||
      input.profilePreviewGateStatus === "resolved" ||
      input.profileUpdatesState === "applied" ||
      input.profileUpdatesState === "dismissed",
    hasPending:
      !hasLocallyResolved &&
      (input.profilePreviewGateStatus === "pending" || input.profileUpdatesState === "pending"),
  };
}

export function hasPendingOnboardingProfilePreviewToolCall(messages: readonly UIMessage[]) {
  return messages.some((message) =>
    message.parts.some((part) => {
      const candidate = part as { type?: string; state?: string };
      return (
        candidate.type === "tool-preview_brand_profile_edits" &&
        (candidate.state === "input-streaming" || candidate.state === "input-available")
      );
    }),
  );
}

function BrandOnboardingChatEmptyState({ sourceDomain }: { sourceDomain: string }) {
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col items-center text-center">
      <img
        src={CHEETAH_SQUARE_LOGO_SRC}
        alt=""
        className="mb-6 h-12 w-12 rounded-2xl shadow-[0_18px_40px_-24px_rgba(136,196,0,0.8)]"
        aria-hidden="true"
      />
      <h1 className="max-w-4xl text-2xl font-black tracking-[-0.04em] text-foreground sm:text-3xl">
        <FormattedMessage
          id="brandOnboarding.chat.emptyTitle"
          defaultMessage="Cheetah is starting with {sourceDomain}"
          values={{ sourceDomain }}
        />
      </h1>
      <p className="mt-4 max-w-3xl text-sm leading-6 text-muted-foreground">
        <FormattedMessage
          id="brandOnboarding.chat.emptyDescription"
          defaultMessage="The first message will be your website URL. Cheetah will keep public research discoveries and profile-edit previews in this conversation."
        />
      </p>
      <div className="mt-7 inline-flex items-center gap-2 rounded-full border border-border/70 bg-card/80 px-4 py-2 text-sm font-semibold text-foreground/72 shadow-soft">
        <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
        <FormattedMessage
          id="brandOnboarding.chat.emptyBadge"
          defaultMessage="Research starts automatically"
        />
      </div>
    </div>
  );
}
